import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { selectCurrentCompany } from "../../../../../redux/features/user/selectors/userState.selectors";
import { useAppSelector } from "../../../../../redux/hooks/redux";
import { formatDate } from "../../../../../utils";
import { colors, alphaVar } from "../../../../ui/colors";

export const JobEventTimeline = ({ events }) => {
  const currentCompany = useAppSelector(selectCurrentCompany);
  if (!events?.length) return null;

  const sorted = [...events].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

  const upcoming = sorted[sorted.length - 1];
  const past = sorted.slice(0, sorted.length - 1);

  return (
    <Box
      sx={{
        //Main container
        position: "relative",
        width: "100%",
      }}
    >
      <Typography //Title
        variant="caption"
        sx={{
          justifySelf: { xs: "center", sm: "flex-start" },
          ml: { xs: 0, sm: 4 },
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 700,
          fontSize: "0.6rem",
          color: colors.gray[400],
          mb: 1.625,
          display: "block",
        }}
      >
        Job Timeline
      </Typography>

      <Stack
        spacing={1}
        sx={{
          // Container for the whole stack of events
          position: "relative",
          zIndex: 2,
          width: "100%",
        }}
      >
        {past // Past events
          .slice()
          .map((event, index) => (
            <Box
              key={event.uid}
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                opacity: 0.45,
              }}
            >
              <CheckCircleIcon
                sx={{
                  alignSelf: "center",
                  fontSize: 20,
                  color: colors.orange.base,
                  flexShrink: 0,
                  opacity: 0.7,
                  zIndex: 3,
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                }}
              />

              <Box
                sx={{
                  ml: { xs: 1.5, sm: 2 },
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row", md: "column", lg: "column", xl: "row" },
                  gap: { xs: 1, sm: 3, md: 1, lg: 1, xl: 3 },
                  alignItems: { xs: "flex-start center", sm: "center", md: "flex-start", xl: "center" },
                  width: { xs: "100%", sm: "100%" },
                  justifyContent: "flex-start",
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight={600}
                  sx={{
                    textTransform: "capitalize",
                    color: colors.gray[600],
                    fontSize: { xs: "0.62rem", sm: "0.76rem" },
                  }}
                >
                  {event.status}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: colors.gray[600],
                    opacity: 0.8,
                    fontSize: { xs: "0.55rem", sm: "0.64rem" },
                    textJustify: "center",
                    textAlign: "baseline",
                    alignSelf: "flex-start center",
                  }}
                >
                  {formatDate(event.startDate, currentCompany?.timeZone, "MMM d, yyyy")}
                </Typography>

                <Chip
                  size="small"
                  label="Completed"
                  sx={{
                    bgcolor: alphaVar(colors.success.base, 0.12),
                    color: colors.success.base,
                    fontSize: "0.6rem",
                    height: { xs: 0, sm: 22 },
                    visibility: { xs: "hidden", sm: "visible" },
                  }}
                />
              </Box>
            </Box>
          ))}

        <Box
          sx={{
            // Actual (upcoming) events
            position: "relative",
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "flex-start",
          }}
        >
          <RadioButtonUncheckedIcon
            sx={{
              alignSelf: "center",
              fontSize: 22,
              color: colors.orange.base,
              flexShrink: 0,
              zIndex: 3,
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
            }}
          />

          <Box
            sx={{
              ml: { xs: 1.5, sm: 2 },
              display: "flex",
              flexDirection: { xs: "column", sm: "row", md: "column", lg: "column", xl: "row" },
              gap: { xs: 1, sm: 3, md: 1, lg: 1, xl: 3 },
              alignItems: { xs: "flex-start center", sm: "center", md: "flex-start", xl: "center" },
              width: { xs: "100%", sm: "100%" },
              justifyContent: "flex-start",
            }}
          >
            <Typography //Upcoming Event Status
              sx={{
                textTransform: "capitalize",
                fontSize: { xs: "0.8rem", sm: "0.9rem" },
                textAlign: "left",
                margin: 0,
              }}
              variant="body1"
              fontWeight={600}
              color={colors.orange.base}
            >
              {upcoming.status}
            </Typography>

            <Typography
              variant="body2"
              color={colors.gray[400]}
              sx={{
                fontSize: { xs: "0.64rem", sm: "0.62rem" },
                textJustify: "center",
                textAlign: { xs: "left", sm: "center", md: "left", xl: "center" },
                alignSelf: "flex-start center",
                fontWeight: 600,
              }}
            >
              {formatDate(upcoming.startDate, currentCompany?.timeZone)}
            </Typography>

            <Chip
              size="small"
              label="Current"
              sx={{
                bgcolor: alphaVar(colors.blue.base, 0.15),
                color: colors.blue.base,
                fontWeight: 600,
                fontSize: "0.6rem",
                height: { xs: 0, sm: 22 },
                visibility: { xs: "hidden", sm: "visible" },
              }}
            />
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};
